import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary that catches chunk-load failures (stale Vite hashes after deploy)
 * and triggers a single hard reload. Uses sessionStorage to prevent infinite reload loops.
 */
export class ChunkLoadErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State | null {
    if (isChunkLoadError(error)) {
      // Only auto-reload once per session to prevent loops
      const key = 'chunk-reload-attempted';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
        return null;
      }
      return { hasError: true };
    }
    // Re-throw non-chunk errors to the next boundary
    throw error;
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center space-y-4 max-w-md px-4">
            <p className="text-lg font-medium">A new version was deployed</p>
            <p className="text-sm text-muted-foreground">
              The page needs a full refresh to load the latest code.
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('chunk-reload-attempted');
                window.location.reload();
              }}
              className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm hover:opacity-90 transition-opacity"
            >
              Reload Now
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function isChunkLoadError(error: Error): boolean {
  const msg = error.message || '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    (error.name === 'TypeError' && msg.includes('fetch'))
  );
}
